/**
 * LVS 77-3:2016 figure 9 horizontal arrow geometry.
 *
 * Phase 1 covers the straight left/right arrows used on sign 705
 * (LVS 77-3:2016 C pielikums, page 165–166). These are pure ribbon
 * polygons — a stem rectangle of width 1A unioned with a chevron-tail
 * head — so the polygon is built analytically without polygon-clipping.
 *
 * Reference numbers:
 *   - A      = 0.5h (compound) or 0.2h (simple)         — figure 9
 *   - ribbon = 1A wide                                  — figure 9
 *   - head   = tip 3A forward of anchor, base 3A,       — figure 9
 *              barbs 0.25A behind anchor
 *   - 705    = total arrow length must be ≥ 2.5h        — page 165
 *
 * The geometry's local origin is the stem-tail center: x = 0 sits at
 * the back of the stem on the central axis, +x runs along the arrow
 * pointing direction (negated for left-pointing arrows so right-pointing
 * +x stays consistent with reading direction).
 */

export type ArrowDirection = 'left' | 'right'
export type ArrowVariant = 'compound' | 'simple'

/** Latvian display labels for the variant enum (used by Controls + ParamsTable). */
export const ARROW_VARIANT_LABELS: Readonly<Record<ArrowVariant, string>> = {
    compound: 'Salikta (A = 0.5h)',
    simple: 'Vienkārša (A = 0.2h)'
}

/** Latvian display labels for the direction enum. ParamsTable adds glyph arrows separately. */
export const ARROW_DIRECTION_LABELS: Readonly<Record<ArrowDirection, string>> = {
    left: 'Pa kreisi',
    right: 'Pa labi'
}

export interface ArrowVertex {
    readonly x: number
    readonly y: number
}

export interface ArrowInput {
    readonly direction: ArrowDirection
    /** Reference height in mm — usually the cap height of the row that the arrow sits next to. */
    readonly h: number
    readonly variant: ArrowVariant
    /**
     * Stem length expressed in A-units. Total arrow length = (stemA + 3) × A.
     * Defaults to 2A so the default total is 5A = 2.5h (compound) — exactly
     * the §705 minimum.
     */
    readonly stemA?: number
}

export interface ArrowBbox {
    readonly x: number
    readonly y: number
    readonly width: number
    readonly height: number
}

export type ArrowWarning =
    | {
          readonly kind: 'h-out-of-spec'
          readonly h: number
      }
    | {
          readonly kind: 'below-min-length-705'
          readonly totalLength: number
          readonly min: number
      }
    | {
          readonly kind: 'stem-too-short'
          readonly stemA: number
      }

export interface ArrowGeometry {
    readonly direction: ArrowDirection
    readonly variant: ArrowVariant
    readonly h: number
    /** A unit in mm. */
    readonly A: number
    readonly stemA: number
    /** Stem length in mm = stemA × A. */
    readonly stemLength: number
    /** Stem ribbon width in mm = 1 × A. */
    readonly stemWidth: number
    /** Head length in mm = 3 × A (notch floor → tip). */
    readonly headLength: number
    /** Head base width in mm = 3 × A. */
    readonly headBase: number
    /** Length the rear barb extends behind the notch floor in mm = 0.25 × A. */
    readonly barbBack: number
    /** Total along-axis extent in mm = stemLength + headLength. */
    readonly totalLength: number
    /**
     * Closed polygon vertices in CW order, local frame. Source of truth for
     * the polygon — non-SVG renderers (jsPDF, sign catalog) consume this list directly.
     */
    readonly vertices: readonly ArrowVertex[]
    /** Local bbox in mm, origin at stem-tail center on the central axis. */
    readonly bbox: ArrowBbox
    readonly warnings: readonly ArrowWarning[]
}

const H_MIN = 100
const H_MAX = 400
const MIN_705_COEF = 2.5
const DEFAULT_STEM_A = 2

function resolveA(h: number, variant: ArrowVariant): number {
    return variant === 'compound' ? 0.5 * h : 0.2 * h
}

/**
 * Builds the seven-vertex arrow polygon for a right-pointing arrow with
 * stem tail at the origin. The polygon is wound clockwise in SVG (y-down)
 * coordinates; the caller mirrors x for the left-pointing variant.
 */
function rightArrowVertices(
    stemLength: number,
    A: number,
    barbBack: number
): readonly ArrowVertex[] {
    const halfStem = A / 2
    const halfBase = 1.5 * A
    const tipX = stemLength + 3 * A
    const barbX = stemLength - barbBack
    return [
        {x: 0, y: -halfStem},
        {x: stemLength, y: -halfStem},
        {x: barbX, y: -halfBase},
        {x: tipX, y: 0},
        {x: barbX, y: halfBase},
        {x: stemLength, y: halfStem},
        {x: 0, y: halfStem}
    ]
}

function mirrorX(verts: readonly ArrowVertex[]): readonly ArrowVertex[] {
    return verts.map(p => ({x: -p.x, y: p.y}))
}

function bboxOf(verts: readonly ArrowVertex[]): ArrowBbox {
    if (verts.length === 0) return {x: 0, y: 0, width: 0, height: 0}
    let minX = verts[0].x
    let maxX = verts[0].x
    let minY = verts[0].y
    let maxY = verts[0].y
    for (let i = 1; i < verts.length; i++) {
        const {x, y} = verts[i]
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
    }
    return {x: minX, y: minY, width: maxX - minX, height: maxY - minY}
}

export function computeArrow(input: ArrowInput): ArrowGeometry {
    const {direction, h, variant} = input
    const stemA = input.stemA ?? DEFAULT_STEM_A
    const A = resolveA(h, variant)

    const stemLength = stemA * A
    const stemWidth = A
    const headLength = 3 * A
    const headBase = 3 * A
    const barbBack = 0.25 * A
    const totalLength = stemLength + headLength

    const rightVerts = rightArrowVertices(stemLength, A, barbBack)
    const vertices = direction === 'right' ? rightVerts : mirrorX(rightVerts)
    const bbox = bboxOf(vertices)

    const warnings: ArrowWarning[] = []
    if (h < H_MIN || h > H_MAX) {
        warnings.push({kind: 'h-out-of-spec', h})
    }
    if (stemA <= 0) {
        warnings.push({kind: 'stem-too-short', stemA})
    }
    const min705 = MIN_705_COEF * h
    if (totalLength + 1e-9 < min705) {
        warnings.push({kind: 'below-min-length-705', totalLength, min: min705})
    }

    return {
        direction,
        variant,
        h,
        A,
        stemA,
        stemLength,
        stemWidth,
        headLength,
        headBase,
        barbBack,
        totalLength,
        vertices,
        bbox,
        warnings
    }
}
